import "@/App.css";
import { lazy, Suspense, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { isChunkLoadError, tryReloadOnce } from "./lib/chunkReload";

// Eager: first-paint / most common entry points.
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";

// Se il caricamento di un chunk fallisce (asset vecchi dopo un deploy, tipico
// su mobile che tiene in memoria la pagina precedente) ricarichiamo la pagina
// una volta per scaricare gli asset aggiornati, evitando la schermata bianca.
const lazyWithRetry = (importer) =>
  lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (isChunkLoadError(error)) {
        tryReloadOnce(); // avvia il reload se il guard temporale lo consente
      }
      // Propaga comunque: se il reload parte navighiamo via, altrimenti
      // l'ErrorBoundary mostra la schermata di recupero (mai spinner infinito).
      throw error;
    }
  });

// Lazy: split every other route into its own chunk so the initial bundle stays
// small. Heavy/rarely-used code (e.g. recharts in the admin dashboard) is only
// downloaded when the user actually navigates there.
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazyWithRetry(() => import("./pages/VerifyEmailPage"));
const ProfileSetupPage = lazyWithRetry(() => import("./pages/ProfileSetupPage"));
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const BookUploadPage = lazyWithRetry(() => import("./pages/BookUploadPage"));
const MyBooksPage = lazyWithRetry(() => import("./pages/MyBooksPage"));
const SearchPage = lazyWithRetry(() => import("./pages/SearchPage"));
const WishlistPage = lazyWithRetry(() => import("./pages/WishlistPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const ChatListPage = lazyWithRetry(() => import("./pages/ChatListPage"));
const ChatDetailPage = lazyWithRetry(() => import("./pages/ChatDetailPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const AdminDashboardPage = lazyWithRetry(() => import("./pages/AdminDashboardPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"));
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage"));
const CookiePage = lazyWithRetry(() => import("./pages/CookiePage"));
const MostWantedPage = lazyWithRetry(() => import("./pages/MostWantedPage"));

// Import api config to set up axios interceptors
import "./lib/api";

const PageLoader = () => (
  <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="page-loader">
    <div className="w-12 h-12 border-4 border-[#FDE047] border-t-[#0A0A0A] rounded-full animate-spin"></div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/accedi" element={<AuthPage />} />
        <Route path="/password-dimenticata" element={<ForgotPasswordPage />} />
        <Route path="/reimposta-password" element={<ResetPasswordPage />} />
        <Route path="/verifica-email" element={<VerifyEmailPage />} />
        <Route path="/termini" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/chi-sono" element={<AboutPage />} />
        <Route path="/cookie" element={<CookiePage />} />
        <Route path="/piu-ricercati" element={<MostWantedPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/setup-profilo" element={<ProtectedRoute requireProfile={false}><ProfileSetupPage /></ProtectedRoute>} />
        <Route path="/carica-libro" element={<ProtectedRoute><BookUploadPage /></ProtectedRoute>} />
        <Route path="/cerca" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/lista-desideri" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
        <Route path="/i-miei-libri" element={<ProtectedRoute><MyBooksPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatListPage /></ProtectedRoute>} />
        <Route path="/chat/:chatId" element={<ProtectedRoute><ChatDetailPage /></ProtectedRoute>} />
        <Route path="/profilo" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/utente/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/impostazioni" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
